import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeenLabel, KeenLabelReason, LabelDeckItem, LabelDeckFilter, LabelReviewFilter, LabelReviewRow } from '@jabberr/core';
import { api, errorMessage } from '../lib/api.js';
import { Empty, ErrorBox, Spinner } from '../components/ui.js';

const REFILL_AT = 5;
const DECK_SIZE = 30;

const REASONS: Array<{ key: string; reason: KeenLabelReason; label: string }> = [
  { key: '1', reason: 'addressed', label: 'Said to Jabberr' },
  { key: '2', reason: 'open_question', label: 'Open question' },
  { key: '3', reason: 'correction', label: 'Worth correcting' },
  { key: '4', reason: 'between_people', label: 'People talking' },
];

const LABEL_TEXT: Record<KeenLabel, string> = { speak: 'Speak', quiet: 'Stay quiet', unsure: 'Not sure' };

/** Help train Jabberr: label real keen moments as flashcards, then review disagreements in a table. */
export function LabelPage() {
  const [savingCard, setSavingCard] = useState(false);
  const [deckFilter, setDeckFilter] = useState<LabelDeckFilter>('all');
  const [tab, setTab] = useState<'cards' | 'review'>('cards');
  return (
    <div className="page label-page">
      <header className="page-head">
        <div>
          <h1>Help train Jabberr</h1>
          <p className="page-subtitle muted small">
            Real moments from your projects. Would you want Jabberr to speak right after the last line? Your answers teach him when to talk.
          </p>
        </div>
        <div className="label-tabs" role="tablist">
          <button type="button" role="tab" disabled={savingCard} aria-selected={tab === 'cards'} className={`tab ${tab === 'cards' ? 'active' : ''}`} onClick={() => setTab('cards')}>
            Cards
          </button>
          <button type="button" role="tab" disabled={savingCard} aria-selected={tab === 'review'} className={`tab ${tab === 'review' ? 'active' : ''}`} onClick={() => setTab('review')}>
            Review
          </button>
        </div>
      </header>
      {tab === 'cards' ? <>
        <div className="label-filters" role="group" aria-label="Turn length">
          {([['all', 'All turns'], ['short', 'Short turns (1–3 words)']] as const).map(([value, label]) => (
            <button key={value} type="button" className={`tab ${deckFilter === value ? 'active' : ''}`}
              disabled={savingCard} aria-pressed={deckFilter === value} onClick={() => setDeckFilter(value)}>{label}</button>
          ))}
        </div>
        <Cards key={deckFilter} filter={deckFilter} saving={savingCard} setSaving={setSavingCard} />
      </> : <Review /> }
    </div>
  );
}

function Cards({ filter, saving, setSaving }: { filter: LabelDeckFilter; saving: boolean; setSaving: (value: boolean) => void }) {
  const [queue, setQueue] = useState<LabelDeckItem[] | null>(null);
  const [history, setHistory] = useState<LabelDeckItem[]>([]);
  const [reason, setReason] = useState<KeenLabelReason | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [session, setSession] = useState(0);
  const [flash, setFlash] = useState<KeenLabel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const answered = useRef(new Set<string>());
  const loading = useRef(false);
  const writing = useRef(false);

  const refill = useCallback(async () => {
    if (loading.current) return;
    loading.current = true;
    try {
      const deck = await api.labelling.deck(DECK_SIZE, filter);
      setRemaining(deck.remaining);
      setQueue((q) => {
        const have = new Set((q ?? []).map((i) => i.key));
        return [...(q ?? []), ...deck.items.filter((i) => !have.has(i.key) && !answered.current.has(i.key))];
      });
    } catch (err) {
      setError(errorMessage(err));
      setQueue((q) => q ?? []);
    } finally {
      loading.current = false;
    }
  }, [filter]);

  useEffect(() => {
    void refill();
  }, [refill]);

  const current = queue?.[0];

  const answer = useCallback(
    async (label: KeenLabel) => {
      if (!current || writing.current) return;
      const item = current;
      writing.current = true;
      setSaving(true);
      setError(null);
      try {
        await api.labelling.put({ key: item.key, label, reason });
        answered.current.add(item.key);
        setQueue((q) => (q ?? []).filter((i) => i.key !== item.key));
        setHistory((h) => [item, ...h].slice(0, 50));
        setReason(null);
        setSession((n) => n + 1);
        setRemaining((n) => Math.max(0, n - 1));
        setFlash(label);
        window.setTimeout(() => setFlash((f) => (f === label ? null : f)), 350);
        if ((queue?.length ?? 0) - 1 <= REFILL_AT) void refill();
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        writing.current = false;
        setSaving(false);
      }
    },
    [current, reason, queue, refill, setSaving],
  );

  const undo = useCallback(async () => {
    const [last, ...rest] = history;
    if (!last || writing.current) return;
    writing.current = true;
    setSaving(true);
    setError(null);
    try {
      await api.labelling.remove(last.key);
      answered.current.delete(last.key);
      setHistory(rest);
      setQueue((q) => [last, ...(q ?? [])]);
      setSession((n) => Math.max(0, n - 1));
      setRemaining((n) => n + 1);
      setReason(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      writing.current = false;
      setSaving(false);
    }
  }, [history, setSaving]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat || writing.current) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k === 'y') answer('speak');
      else if (k === 'n') answer('quiet');
      else if (k === ' ') answer('unsure');
      else if (k === 'backspace' || k === 'u') undo();
      else {
        const r = REASONS.find((x) => x.key === k);
        if (!r) return;
        setReason((cur) => (cur === r.reason ? null : r.reason));
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [answer, undo]);

  if (queue === null) return <Spinner label="Dealing cards" />;

  return (
    <div className="label-cards">
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      <div className="label-progress muted small" aria-live="polite">
        <span>{session} this session</span>
        <span>{remaining} left</span>
        {history.length > 0 ? (
          <button type="button" className="link-btn" disabled={saving} onClick={() => void undo()}>
            Undo last (Backspace)
          </button>
        ) : null}
      </div>

      {current ? (
        <article className={`card label-card ${flash ? `flash-${flash}` : ''}`} aria-label="Moment to label">
          <div className="label-meta">
            <span className="chip">{current.source === 'meeting' ? 'Meeting' : 'Text thread'}</span>
            <span className="muted small">{current.projectName}</span>
            {current.gapBeforeS !== null ? <span className="muted small">{current.gapBeforeS.toFixed(1)} s pause before</span> : null}
          </div>
          <ol className="label-lines">
            {current.context.map((line, i) => (
              <li key={i} className={`${i === current.context.length - 1 ? 'judged' : ''} ${line.speaker === 'Jabberr' ? 'from-jabberr' : ''}`}>
                <span className="label-speaker">{line.speaker}</span>
                <span className="label-text">{line.text}</span>
              </li>
            ))}
          </ol>
          <p className="label-question" aria-live="polite">{saving ? 'Saving answer…' : 'Should Jabberr speak now?'}</p>
          <div className="label-reasons" aria-label="Reason (optional)">
            {REASONS.map((r) => (
              <button
                key={r.reason}
                type="button"
                className={`chip label-reason ${reason === r.reason ? 'on' : ''}`}
                disabled={saving}
                aria-pressed={reason === r.reason}
                onClick={() => setReason((cur) => (cur === r.reason ? null : r.reason))}
              >
                <kbd>{r.key}</kbd> {r.label}
              </button>
            ))}
          </div>
          <div className="label-answers">
            <button type="button" className="btn label-answer speak" disabled={saving} onClick={() => void answer('speak')}>
              <kbd>Y</kbd> Speak
            </button>
            <button type="button" className="btn label-answer quiet" disabled={saving} onClick={() => void answer('quiet')}>
              <kbd>N</kbd> Stay quiet
            </button>
            <button type="button" className="btn label-answer unsure" disabled={saving} onClick={() => void answer('unsure')}>
              <kbd>Space</kbd> Not sure
            </button>
          </div>
        </article>
      ) : (
        <Empty>{session > 0 ? `Nothing left to label. Thank you: ${session} answers this session.` : 'Nothing to label yet. Moments appear here after meetings and keen threads in your projects.'}</Empty>
      )}
    </div>
  );
}

function Review() {
  const [filter, setFilter] = useState<LabelReviewFilter>('disagree');
  const [rows, setRows] = useState<LabelReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: LabelReviewFilter) => {
    setRows(null);
    try {
      setRows((await api.labelling.review(f)).rows);
    } catch (err) {
      setError(errorMessage(err));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  const relabel = async (row: LabelReviewRow, label: KeenLabel): Promise<void> => {
    try {
      await api.labelling.put({ key: row.key, label, reason: row.mine?.reason ?? null });
      setRows((rs) => (rs ?? []).map((r) => (r.key === row.key ? { ...r, mine: { label, reason: r.mine?.reason ?? null } } : r)));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="label-review">
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      <div className="label-filters" role="radiogroup" aria-label="Show">
        {(
          [
            ['disagree', 'Disagreements'],
            ['unsure', 'Not sure'],
            ['all', 'Everything labelled'],
          ] as const
        ).map(([f, text]) => (
          <button key={f} type="button" role="radio" aria-checked={filter === f} className={`tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {text}
          </button>
        ))}
      </div>
      {rows === null ? (
        <Spinner label="Loading" />
      ) : rows.length === 0 ? (
        <Empty>Nothing here yet.</Empty>
      ) : (
        <div className="label-table-wrap">
          <table className="label-table">
            <thead>
              <tr>
                <th>Moment</th>
                <th>You</th>
                <th>Others</th>
                <th>Decision outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="label-moment">
                    <span className="muted small">
                      {row.source === 'meeting' ? 'Meeting' : 'Text'} · {row.projectName}
                    </span>
                    {row.context.slice(-3).map((line, i, arr) => (
                      <div key={i} className={i === arr.length - 1 ? 'judged' : 'muted'}>
                        <b>{line.speaker}:</b> {line.text}
                      </div>
                    ))}
                  </td>
                  <td>
                    <div className="label-mine" role="radiogroup" aria-label="Your answer">
                      {(['speak', 'quiet', 'unsure'] as const).map((l) => (
                        <button
                          key={l}
                          type="button"
                          role="radio"
                          aria-checked={row.mine?.label === l}
                          className={`chip label-choice ${l} ${row.mine?.label === l ? 'on' : ''}`}
                          onClick={() => void relabel(row, l)}
                        >
                          {LABEL_TEXT[l]}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="small">{row.others.length ? row.others.map((o) => `${o.name}: ${LABEL_TEXT[o.label]}`).join(', ') : <span className="muted">none</span>}</td>
                  <td className="small">{decisionOutcome(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** A positive verdict starts a reply; only the voice worker can prove playback. */
function decisionOutcome(row: LabelReviewRow): string {
  const outcomes = {
    spoke: 'Reply started', quiet: 'Stayed quiet', superseded: 'Superseded by newer speech',
    busy: 'Another reply in progress', skipped_rule: 'Skipped by rule', timeout: 'Decision timed out',
    aborted: 'Decision cancelled', unavailable: 'Decider unavailable', error: 'Decision failed', unparsed: 'Decision unreadable',
  };
  if (row.deciderOutcome) return outcomes[row.deciderOutcome];
  return row.deciderSpoke === null ? 'Not recorded' : row.deciderSpoke ? 'Positive verdict; delivery unknown' : 'Quiet verdict; delivery unknown';
}
